import { useState } from "react";
import { X } from "lucide-react";
import { Reveal, Label } from "./Reveal";

const IMG = "/founder.webp";

const INTRO = [
  "With over 15 years of experience across law, ethics and leadership, Priya Rawal has built her career working with leading multinational organisations, advising on complex matters, working with diverse stakeholders and helping organisations navigate decisions with sound judgement and integrity.",
  "A lawyer by profession and an Ethics Leader by experience, Priya has spent much of her career understanding people, behaviour, decision-making and the principles that influence how we work and lead. She is an International Coach Federation (ICF) Certified Life Coach and a Certified Positive Parenting Coach, bringing together professional expertise, coaching methodology and her experience as a mother.",
];

const SECTIONS = [
  {
    h: "A Mothers-First Approach",
    paras: [
      "Priya's work with mothers begins with a simple premise: before we focus on the child, we need to make space for the mother.",
      "Motherhood can bring profound change, but it can also leave little room for a woman to pause and consider her own identity, aspirations, relationships, emotional patterns and wellbeing.",
      "Through personal transformation coaching, Priya works with mothers to develop greater self-awareness, gain clarity, understand recurring patterns, strengthen boundaries and make more conscious choices, personally, professionally and within their relationships.",
      "The focus is not on becoming a different person. It is on understanding yourself better and creating a life that is more aligned with who you are.",
    ],
  },
  {
    h: "Conscious Parenting",
    paras: [
      "Her parenting practice is grounded in a conscious mindset, moving beyond simply managing behaviour to understanding the child, the parent and the relationship between them.",
      "Priya believes that the more conscious a parent becomes of themselves, the more consciously they can parent, and the more their parenting can thrive.",
      "Through personalised parenting coaching, she helps parents navigate concerns such as challenging behaviour, big emotions, boundaries, communication, confidence, peer relationships, transitions and other everyday parenting situations.",
      "Her approach encourages parents to pause, understand what may be driving a situation and respond with greater awareness and intention. The goal is not perfect parenting. It is more conscious parenting.",
    ],
  },
  {
    h: "From Corporate Leadership to Purpose-Led Entrepreneurship",
    paras: [
      "After more than 15 years in the corporate world, Mended Alchemy represents a pivot with purpose, bringing Priya's experience in law, ethics, leadership and coaching into a venture focused on personal growth and parenting.",
      "Her first ebook, Mumma Can Heal Me, reflects her belief in the importance of a mother's own inner work and the role it can play in creating a healthier parenting experience.",
      "Today, Priya combines the perspective of a seasoned professional with the personal understanding of a mother and the structured approach of a coach. Through Mended Alchemy, she is building a trusted space for mothers seeking personal growth and parents seeking a more conscious approach to raising their children.",
    ],
  },
];

const PURPOSE = "To help mothers thrive as individuals, support parents in navigating parenting with greater consciousness, and contribute to healthier, more connected families.";

export default function About({ onBook }) {
  const [storyOpen, setStoryOpen] = useState(false);

  return (
    <section
      id="about-section"
      data-testid="about-section"
      className="py-20 lg:py-32 px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto"
    >
      <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <Reveal className="lg:col-span-5" y={40}>
          <div className="relative overflow-hidden clip-frame-alt border border-[#2F3B2E]/15 shadow-[0_30px_60px_-25px_rgba(47,59,46,0.4)]">
            <img src={IMG} alt="Priya Rawal, Founder, Mended Alchemy" className="w-full h-[420px] lg:h-[520px] object-cover" />
          </div>
        </Reveal>

        <Reveal delay={0.12} className="lg:col-span-7">
          <Label>Meet the Founder</Label>
          <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight leading-tight text-[#3B352D]">
            Priya Rawal
          </h2>
          <p className="mt-3 font-mono-accent text-[11px] sm:text-xs uppercase tracking-[0.18em] text-[#A8916B]">
            Founder, Mended Alchemy | Life &amp; Parenting Coach | Author | Mother
          </p>
          <p className="mt-6 text-base sm:text-lg leading-relaxed text-[#3B352D]/85">
            {INTRO[0]}
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3.5">
            <button
              onClick={() => setStoryOpen(true)}
              data-testid="about-read-more"
              className="inline-flex items-center justify-center rounded-full bg-[#2F3B2E] px-7 py-3.5 text-sm font-medium text-white hover:bg-[#232B22] transition-colors duration-200"
            >
              Read Priya's Story
            </button>
            <button
              onClick={onBook}
              data-testid="about-book-button"
              className="inline-flex items-center justify-center rounded-full border border-[#2F3B2E]/25 px-7 py-3.5 text-sm font-medium text-[#2F3B2E] hover:bg-[#2F3B2E] hover:text-white transition-colors duration-200"
            >
              Book a Consultation
            </button>
          </div>
        </Reveal>
      </div>

      {/* Full founder story overlay */}
      {storyOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-testid="about-story-modal">
          <div className="relative w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-3xl bg-[#FAF7F0] border border-[#2F3B2E]/15 p-7 lg:p-10">
            <button onClick={() => setStoryOpen(false)} data-testid="about-story-close" aria-label="Close story" className="absolute top-5 right-5 text-[#2F3B2E]/60 hover:text-[#2F3B2E] text-sm z-10">Close ✕</button>

            <div className="flex items-center gap-4">
              <img src={IMG} alt="Priya Rawal" className="h-20 w-20 rounded-full object-cover object-[50%_20%] border-2 border-[#C9B48A]" />
              <div>
                <p className="font-mono-accent text-[10px] uppercase tracking-[0.22em] text-[#A8916B]">Meet the Founder</p>
                <h3 className="font-serif-display text-2xl font-medium text-[#3B352D]">Priya Rawal</h3>
                <p className="font-mono-accent text-[9px] uppercase tracking-[0.16em] text-[#6B665C] mt-0.5">Founder | Life &amp; Parenting Coach | Author | Mother</p>
              </div>
            </div>

            <div className="mt-7 space-y-5 text-base leading-[1.8] text-[#3B352D]/85">
              {INTRO.map((p, i) => <p key={i}>{p}</p>)}
              <p className="font-serif-display italic text-[#2F3B2E]">
                These experiences form the foundation of Mended Alchemy.
              </p>
            </div>

            {SECTIONS.map((s) => (
              <div key={s.h} className="mt-9">
                <h4 className="font-serif-display text-xl sm:text-2xl font-medium text-[#2F3B2E] tracking-tight">{s.h}</h4>
                <div className="mt-3 space-y-3.5 text-[15px] leading-[1.8] text-[#3B352D]/80">
                  {s.paras.map((p, j) => <p key={j}>{p}</p>)}
                </div>
              </div>
            ))}

            <div className="mt-9 rounded-3xl bg-[#2F3B2E] px-7 py-8">
              <p className="font-mono-accent text-[11px] uppercase tracking-[0.25em] text-[#C9B48A]">Her purpose is clear</p>
              <p className="mt-3 font-serif-display text-lg sm:text-xl leading-relaxed text-[#F2EAD9]">{PURPOSE}</p>
              <p className="mt-4 text-sm text-white/70">Priya Rawal, Founder, Mended Alchemy</p>
            </div>

            <button
              onClick={() => { setStoryOpen(false); onBook?.(); }}
              data-testid="about-story-book"
              className="mt-7 w-full rounded-full bg-[#2F3B2E] py-3.5 text-sm font-medium text-white hover:bg-[#232B22] transition-colors duration-200"
            >
              Book a Consultation
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
